import React, { useState } from "react";
import {
  EuiInMemoryTable,
  EuiBasicTableColumn,
  EuiLink,
  EuiProgress,
  EuiFlexGroup,
  EuiFlexItem,
  EuiBadge,
  EuiToolTip,
  EuiText,
  EuiPanel,
  EuiButtonIcon,
  EuiPopover,
  EuiListGroup,
  EuiListGroupItem,
  EuiHorizontalRule,
  EuiIcon,
  EuiTitle,
  EuiButtonGroup,
  EuiSpacer,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { OpsanioWaves } from "src/questionnaires";
import { useApi } from "src/api";
import { AnketaActionsEnum, AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useOpsanioCan } from "../../can";
import { useToast } from "src/notifications";
import { useAnketaI18n } from "src/internationalization";
import { TableSkeleton } from "src/components/skeleton";
import { FleetHealthEntry } from "../useFleetHealth";
import { EMPTY_HEALTH, HEALTH_COLOR, HEALTH_ICON } from "../health";
import { waveColor } from "../waves";
import { relativeLabel } from "../format";

const formatDate = (iso: string) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ContactsPopover: React.FC<{
  contacts: string[];
  label: string;
  ariaLabel: string;
}> = ({ contacts, label, ariaLabel }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!contacts?.length) return <EuiText size="s">-</EuiText>;

  return (
    <EuiPopover
      button={
        <EuiButtonIcon
          iconType="email"
          aria-label={ariaLabel}
          onClick={() => setIsOpen((v) => !v)}
          size="s"
        />
      }
      isOpen={isOpen}
      closePopover={() => setIsOpen(false)}
      panelPaddingSize="s"
    >
      <EuiTitle size="xxxs">
        <h4>{label}</h4>
      </EuiTitle>
      <EuiHorizontalRule margin="xs" />
      <EuiListGroup flush maxWidth={240}>
        {contacts.map((c) => (
          <EuiListGroupItem key={c} label={c} size="s" />
        ))}
      </EuiListGroup>
    </EuiPopover>
  );
};

/**
 * Which column set the table shows. Ops = live health first (status, coverage,
 * incidents, tickets, last sync); Provisioning = the delivery/CRM view (VPC,
 * location, agents, contacts, dates); All = everything at once. Every column
 * stays reachable - the toggle only chooses what is on screen by default.
 */
export type ApplianceColumnSet = "ops" | "provisioning" | "all";

interface AppliancesTableProps {
  appliances: OpsanioApplianceData[];
  loading?: boolean;
  updateSelectedItem: (item: OpsanioApplianceData) => void;
  reload?: () => void;
  /** Live health per appliance id; absent entries render as unmonitored. */
  healthById?: Record<string, FleetHealthEntry>;
  /** The health sweep is still running - incident/status cells say so. */
  healthLoading?: boolean;
  /** Appliance whose flyout is open - its row is highlighted. */
  selectedId?: string | null;
  /**
   * The filter bar, rendered inside the table panel. Receives the column-set
   * switch so both sit in one toolbar row (there is ONE search: the table's
   * own search box was a second, disconnected one).
   */
  renderToolbar?: (columnSetSwitch: React.ReactNode) => React.ReactNode;
}

const AppliancesTable: React.FC<AppliancesTableProps> = ({
  appliances = [],
  loading = false,
  updateSelectedItem,
  reload,
  healthById = {},
  healthLoading = false,
  selectedId = null,
  renderToolbar,
}) => {
  const { Put } = useApi();
  const { keycloak } = useAnketaAuth();
  const can = useOpsanioCan();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();
  const [columnSet, setColumnSet] = useState<ApplianceColumnSet>("ops");

  // Archive/restore mirror the server rule: archive·all = admin AND isAutonubil
  // (AUTH_RECOMMENDATION.md §5). UI affordance only - the route is the boundary.
  // Same rule as the server: archive · {isAutonubil}, or superuser.
  const canArchive = can(
    AnketaResourcesEnum.questionnaires,
    AnketaActionsEnum.archive,
    { isAutonubil: true },
  );

  const setArchived = (item: OpsanioApplianceData, archived: boolean) => {
    Put({
      path: `v2/${AnketaResourcesEnum.questionnaires}/${item.catalogId}/${item.id}/archive`,
      value: { archived },
      responseType: "json",
      token: keycloak.token,
    })
      .then((resp) => {
        // useApi never rejects: a 403/500 resolves with success=false.
        if (!resp.success) throw resp.error ?? new Error("archive failed");
        addToast({
          color: "success",
          iconType: archived ? "folderClosed" : "folderOpen",
          title: archived
            ? translate("anketa.opsanio.table.toast.archived.title", {
                defaultMessage: "Appliance archived",
              })
            : translate("anketa.opsanio.table.toast.restored.title", {
                defaultMessage: "Appliance restored",
              }),
          text: item.displayName,
        });
        reload?.();
      })
      .catch(() => {
        addToast({
          color: "danger",
          iconType: "alert",
          title: translate("anketa.opsanio.table.toast.archive.error.title", {
            defaultMessage: "Failed to update archive state",
          }),
          text: item.displayName,
        });
      });
  };

  const statusLabel: Record<string, string> = {
    healthy: translate("anketa.opsanio.overview.status.healthy", {
      defaultMessage: "Healthy",
    }),
    warning: translate("anketa.opsanio.overview.status.warning", {
      defaultMessage: "Warning",
    }),
    critical: translate("anketa.opsanio.overview.status.critical", {
      defaultMessage: "Critical",
    }),
    unknown: translate("anketa.opsanio.overview.status.unmonitored", {
      defaultMessage: "Unmonitored",
    }),
  };

  const healthOf = (item: OpsanioApplianceData) =>
    healthById[item.id]?.health ?? EMPTY_HEALTH;

  /**
   * Live-health columns. An appliance with no entry yet (sweep still running, or
   * skipped because it is archived / has no VPC name) reads as unmonitored and
   * its incident count as unknown - never as a healthy zero.
   */
  const opsColumns: Array<EuiBasicTableColumn<OpsanioApplianceData>> = [
    {
      name: translate("anketa.opsanio.table.column.status", {
        defaultMessage: "Status",
      }),
      width: "130px",
      render: (item: OpsanioApplianceData) => {
        const health = healthOf(item);
        return (
          <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiIcon
                type={HEALTH_ICON[health.status]}
                color={HEALTH_COLOR[health.status]}
                size="s"
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size="xs">{statusLabel[health.status]}</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        );
      },
    },
    {
      name: translate("anketa.opsanio.table.column.monitoring", {
        defaultMessage: "Monitoring",
      }),
      width: "170px",
      render: (item: OpsanioApplianceData) => {
        const health = healthOf(item);
        if (!health.monitored) {
          return (
            <EuiText size="xs" color="subdued">
              {healthLoading
                ? translate("anketa.opsanio.table.cell.loading", {
                    defaultMessage: "loading…",
                  })
                : translate("anketa.opsanio.table.cell.noSensors", {
                    defaultMessage: "no sensors",
                  })}
            </EuiText>
          );
        }
        const counts = health.counts;
        const up = counts?.up ?? 0;
        const total = counts?.total ?? 0;
        return (
          <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
            <EuiFlexItem>
              <EuiProgress
                value={up}
                max={total || 1}
                size="s"
                color={HEALTH_COLOR[health.status]}
                aria-label={translate("anketa.opsanio.table.cell.sensorsUp", {
                  values: { up, total },
                  defaultMessage: "{{up}}/{{total}} up",
                })}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {up}/{total}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        );
      },
    },
    {
      name: translate("anketa.opsanio.table.column.incidents", {
        defaultMessage: "Incidents",
      }),
      width: "100px",
      render: (item: OpsanioApplianceData) => {
        const health = healthOf(item);
        if (!health.monitored) {
          return (
            <EuiText size="xs" color="subdued">
              {translate("anketa.opsanio.table.cell.unknown", {
                defaultMessage: "unknown",
              })}
            </EuiText>
          );
        }
        // Only a non-zero count deserves colour; a green "0" badge in every
        // row drowned out the rows that matter.
        return health.activeIncidents > 0 ? (
          <EuiBadge color="danger">{health.activeIncidents}</EuiBadge>
        ) : (
          <EuiText size="xs" color="subdued">
            0
          </EuiText>
        );
      },
    },
    {
      name: translate("anketa.opsanio.table.column.support", {
        defaultMessage: "Support",
      }),
      width: "150px",
      render: (item: OpsanioApplianceData) => {
        const engagement = healthById[item.id]?.engagement;
        if (!engagement) {
          return (
            <EuiText size="xs" color="subdued">
              -
            </EuiText>
          );
        }
        // Labelled counts ("2 Tickets · 1 Issue") instead of a bare "2 / 1".
        const parts: string[] = [];
        if (engagement.otoboOpen > 0) {
          parts.push(
            engagement.otoboOpen === 1
              ? translate("anketa.opsanio.table.cell.tickets.one", {
                  defaultMessage: "1 ticket",
                })
              : translate("anketa.opsanio.table.cell.tickets.other", {
                  values: { count: engagement.otoboOpen },
                  defaultMessage: "{{count}} tickets",
                }),
          );
        }
        if (engagement.gitlabOpen > 0) {
          parts.push(
            engagement.gitlabOpen === 1
              ? translate("anketa.opsanio.table.cell.issues.one", {
                  defaultMessage: "1 issue",
                })
              : translate("anketa.opsanio.table.cell.issues.other", {
                  values: { count: engagement.gitlabOpen },
                  defaultMessage: "{{count}} issues",
                }),
          );
        }
        return (
          <EuiText size="xs" color={parts.length ? "default" : "subdued"}>
            {parts.length ? parts.join(" · ") : "-"}
          </EuiText>
        );
      },
    },
    {
      name: translate("anketa.opsanio.table.column.lastSync", {
        defaultMessage: "Last sync",
      }),
      width: "110px",
      render: (item: OpsanioApplianceData) => {
        const health = healthOf(item);
        return (
          <EuiText size="xs" color="subdued">
            {health.lastSync
              ? relativeLabel(health.lastSync, translate)
              : translate("anketa.opsanio.table.cell.neverSynced", {
                  defaultMessage: "never synced",
                })}
          </EuiText>
        );
      },
    },
  ];

  const identityColumns: Array<EuiBasicTableColumn<OpsanioApplianceData>> = [
    {
      field: "displayName",
      name: translate("anketa.opsanio.table.column.appliance", {
        defaultMessage: "Appliance",
      }),
      sortable: true,
      truncateText: true,
      width: "240px",
      render: (displayName: string, item: OpsanioApplianceData) => {
        const loc = item.common?.location;
        const subline = [
          item.common?.metadata?.project,
          [loc?.city, loc?.country].filter(Boolean).join(", "),
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
            <EuiFlexItem>
              <EuiText size="s">
                {/* The name opens the flyout - the eye icon alone was easy
                    to miss. */}
                <EuiLink onClick={() => updateSelectedItem(item)}>
                  <strong>{displayName || item.id}</strong>
                </EuiLink>{" "}
                {item.archived && (
                  <EuiBadge color="hollow" iconType="folderClosed">
                    {translate("anketa.opsanio.table.badge.archived", {
                      defaultMessage: "Archived",
                    })}
                  </EuiBadge>
                )}
              </EuiText>
              <EuiText size="xs" color="subdued">
                {subline || "-"}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        );
      },
    },
    {
      field: "wave",
      name: translate("anketa.opsanio.table.column.wave", {
        defaultMessage: "Wave",
      }),
      sortable: true,
      width: "90px",
      render: (wave: OpsanioWaves) => {
        if (!wave) return "-";
        return <EuiBadge color={waveColor(wave)}>{wave}</EuiBadge>;
      },
    },
  ];

  /** Delivery / CRM view - unchanged, just no longer always on screen. */
  const provisioningColumns: Array<EuiBasicTableColumn<OpsanioApplianceData>> = [
    {
      field: "common.metadata.name",
      name: translate("anketa.opsanio.table.column.vpcName", {
        defaultMessage: "VPC Name",
      }),
      sortable: true,
      truncateText: true,
      render: (_: unknown, item: OpsanioApplianceData) =>
        item.common?.metadata?.name ?? "-",
    },
    {
      field: "common.location",
      name: translate("anketa.opsanio.table.column.location", {
        defaultMessage: "Location",
      }),
      sortable: false,
      width: "150px",
      render: (_: unknown, item: OpsanioApplianceData) => {
        const loc = item.common?.location;
        if (!loc) return "-";
        return (
          <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiIcon type="globe" size="s" />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiText size="s">
                {[loc.city, loc.country].filter(Boolean).join(", ") || "-"}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        );
      },
    },
    {
      field: "common.counts",
      name: translate("anketa.opsanio.table.column.agentsServers", {
        defaultMessage: "Agents / Servers",
      }),
      sortable: false,
      width: "130px",
      render: (_: unknown, item: OpsanioApplianceData) => {
        const counts = item.common?.counts;
        if (!counts) return "-";
        return (
          <EuiFlexGroup gutterSize="s" responsive={false}>
            <EuiText>
              <p>
                {counts.agents ?? 0}/{counts.server ?? 0}
              </p>
            </EuiText>
          </EuiFlexGroup>
        );
      },
    },
    {
      field: "common.general.contacts",
      name: translate("anketa.opsanio.table.column.contacts", {
        defaultMessage: "Contacts",
      }),
      sortable: false,
      width: "110px",
      render: (_: unknown, item: OpsanioApplianceData) => {
        const contacts = item.common?.general?.contacts;
        const salesLabel = translate("anketa.opsanio.table.contacts.sales", {
          defaultMessage: "Sales",
        });
        const technicalLabel = translate(
          "anketa.opsanio.table.contacts.technical",
          { defaultMessage: "Technical" },
        );
        const customerLabel = translate(
          "anketa.opsanio.table.contacts.customer",
          { defaultMessage: "Customer" },
        );
        return (
          <EuiFlexGroup gutterSize="xs" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiToolTip content={salesLabel}>
                <ContactsPopover
                  contacts={contacts?.sales ?? []}
                  label={salesLabel}
                  ariaLabel={translate(
                    "anketa.opsanio.table.contacts.view.aria",
                    {
                      values: { label: salesLabel },
                      defaultMessage: "View {{label}} contacts",
                    },
                  )}
                />
              </EuiToolTip>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiToolTip content={technicalLabel}>
                <ContactsPopover
                  contacts={contacts?.technical ?? []}
                  label={technicalLabel}
                  ariaLabel={translate(
                    "anketa.opsanio.table.contacts.view.aria",
                    {
                      values: { label: technicalLabel },
                      defaultMessage: "View {{label}} contacts",
                    },
                  )}
                />
              </EuiToolTip>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiToolTip content={customerLabel}>
                <ContactsPopover
                  contacts={contacts?.customer ?? []}
                  label={customerLabel}
                  ariaLabel={translate(
                    "anketa.opsanio.table.contacts.view.aria",
                    {
                      values: { label: customerLabel },
                      defaultMessage: "View {{label}} contacts",
                    },
                  )}
                />
              </EuiToolTip>
            </EuiFlexItem>
          </EuiFlexGroup>
        );
      },
    },
    {
      field: "common.general.dates",
      name: translate("anketa.opsanio.table.column.projectDates", {
        defaultMessage: "Project Dates",
      }),
      sortable: false,
      width: "160px",
      render: (_: unknown, item: OpsanioApplianceData) => {
        const dates = item.common?.general?.dates;
        if (!dates) return "-";
        return (
          <EuiText size="xs">
            <div>
              {translate("anketa.opsanio.table.dates.start", {
                values: { date: formatDate(dates.start) },
                defaultMessage: "Start: {{date}}",
              })}
            </div>
            <div>
              {translate("anketa.opsanio.table.dates.end", {
                values: { date: formatDate(dates.end) },
                defaultMessage: "End: {{date}}",
              })}
            </div>
          </EuiText>
        );
      },
    },
    {
      field: "createdAt",
      name: translate("anketa.opsanio.table.column.created", {
        defaultMessage: "Created",
      }),
      sortable: true,
      width: "110px",
      render: (val: string) => (
        <EuiText size="xs" color="subdued">
          {formatDate(val)}
        </EuiText>
      ),
    },
    {
      field: "updatedAt",
      name: translate("anketa.opsanio.table.column.updated", {
        defaultMessage: "Updated",
      }),
      sortable: true,
      width: "110px",
      render: (val: string) => (
        <EuiText size="xs" color="subdued">
          {formatDate(val)}
        </EuiText>
      ),
    },
  ];

  const actionColumns: Array<EuiBasicTableColumn<OpsanioApplianceData>> = [
    {
      name: translate("anketa.opsanio.table.column.actions", {
        defaultMessage: "Actions",
      }),
      width: "90px",
      actions: [
        {
          name: translate("anketa.opsanio.table.action.view.name", {
            defaultMessage: "View",
          }),
          description: translate("anketa.opsanio.table.action.view.description", {
            defaultMessage: "View appliance details",
          }),
          icon: "arrowRight",
          type: "icon",
          onClick: (item: OpsanioApplianceData) => updateSelectedItem(item),
        },
        {
          name: translate("anketa.opsanio.table.action.archive.name", {
            defaultMessage: "Archive",
          }),
          description: translate(
            "anketa.opsanio.table.action.archive.description",
            { defaultMessage: "Archive this appliance" },
          ),
          icon: "folderClosed",
          type: "icon",
          available: (item: OpsanioApplianceData) => canArchive && !item.archived,
          onClick: (item: OpsanioApplianceData) => setArchived(item, true),
        },
        {
          name: translate("anketa.opsanio.table.action.restore.name", {
            defaultMessage: "Restore",
          }),
          description: translate(
            "anketa.opsanio.table.action.restore.description",
            { defaultMessage: "Restore this appliance" },
          ),
          icon: "folderOpen",
          type: "icon",
          available: (item: OpsanioApplianceData) => canArchive && !!item.archived,
          onClick: (item: OpsanioApplianceData) => setArchived(item, false),
        },
      ],
    },
  ];

  // Identity is always visible; the toggle only decides whether the live-health
  // columns, the provisioning columns, or both follow it.
  const columns: Array<EuiBasicTableColumn<OpsanioApplianceData>> =
    columnSet === "ops"
      ? [...identityColumns, ...opsColumns, ...actionColumns]
      : columnSet === "provisioning"
        ? [...identityColumns, ...provisioningColumns, ...actionColumns]
        : [
            ...identityColumns,
            ...opsColumns,
            ...provisioningColumns,
            ...actionColumns,
          ];

  const columnSetOptions = [
    {
      id: "ops",
      label: translate("anketa.opsanio.table.columnSet.ops", {
        defaultMessage: "Operations",
      }),
    },
    {
      id: "provisioning",
      label: translate("anketa.opsanio.table.columnSet.provisioning", {
        defaultMessage: "Provisioning",
      }),
    },
    {
      id: "all",
      label: translate("anketa.opsanio.table.columnSet.all", {
        defaultMessage: "All",
      }),
    },
  ];

  const columnSetSwitch = (
    <EuiButtonGroup
      legend={translate("anketa.opsanio.table.columnSet.legend", {
        defaultMessage: "Columns",
      })}
      options={columnSetOptions}
      idSelected={columnSet}
      onChange={(id: string) => setColumnSet(id as ApplianceColumnSet)}
      buttonSize="compressed"
      color="primary"
    />
  );

  // A click anywhere on a row opens its flyout - except on the row's own
  // controls (name link, contact popovers, archive/restore actions).
  const rowProps = (item: OpsanioApplianceData) => ({
    isSelected: selectedId === item.id,
    style: { cursor: "pointer" },
    onClick: (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("a, button, input, [role='button']")) return;
      updateSelectedItem(item);
    },
  });

  return (
    <EuiPanel hasBorder paddingSize="m">
      {renderToolbar ? (
        renderToolbar(columnSetSwitch)
      ) : (
        <EuiFlexGroup justifyContent="flexEnd" responsive={false}>
          <EuiFlexItem grow={false}>{columnSetSwitch}</EuiFlexItem>
        </EuiFlexGroup>
      )}
      <EuiSpacer size="s" />
      {loading && appliances.length === 0 ? (
        <TableSkeleton rows={8} />
      ) : (
        <EuiInMemoryTable<OpsanioApplianceData>
          items={appliances}
          columns={columns}
          loading={loading}
          rowProps={rowProps}
          pagination={{
            initialPageSize: 10,
            pageSizeOptions: [10, 25, 50],
          }}
          sorting={{
            sort: {
              field: "createdAt",
              direction: "desc",
            },
          }}
          rowHeader="displayName"
          tableLayout="auto"
          message={
            appliances.length === 0
              ? translate("anketa.opsanio.table.empty.noMatch", {
                  defaultMessage: "No appliances match your filters.",
                })
              : translate("anketa.opsanio.table.empty.none", {
                  defaultMessage: "No appliances found.",
                })
          }
        />
      )}
    </EuiPanel>
  );
};

export default AppliancesTable;
